import { useState, useEffect } from "react";
import { motion } from "framer-motion";

export default function PlantAnimation() {
  const [isComplete, setIsComplete] = useState(false);

  // Reset animation when clicking anywhere
  const resetAnimation = () => {
    setIsComplete(false);
    setTimeout(() => setIsComplete(true), 100);
  };

  useEffect(() => {
    // Start animation on initial load
    setIsComplete(true);
  }, []);

  // Define the leaves with their properties
  // Order is important for the animation sequence
  const leaves = [
    // Center stem
    {
      d: "M176 572L254.5 91.5L268.5 45L286.5 0L304.5 45L320 91.5L389 572H176Z",
      fill: "#5FBC80",
      delay: 0,
      duration: 0.75, // Faster
    },
    // Right half of center stem
    {
      d: "M389 572H286.5V0L304.5 45L320 91.5L389 572Z",
      fill: "#1EA25E",
      delay: 0.2, // Faster
      duration: 0.65, // Faster
    },
    // Left green leaf
    {
      d: "M175 572.5L125 47.5L160 89.5L187 134.5L232 224L371.5 572.5H175Z",
      fill: "#1FA25D",
      delay: 0.4, // Faster
      duration: 0.6, // Faster
    },
    // Right light green leaf
    {
      d: "M371.5 572L455.5 60L426 93L401.5 127.5L359.5 209L203.5 572H371.5Z",
      fill: "#92D153",
      delay: 0.6, // Faster
      duration: 0.6, // Faster
    },
    // Inner light green leaf
    {
      d: "M430 322L463 162.5L430.5 191.5L399 229L347.5 314.5L197.5 572H340L430 322Z",
      fill: "#A8DC54",
      delay: 0.8, // Faster
      duration: 0.55, // Faster
    },
    // Left yellow-green leaf
    {
      d: "M320.5 572H193.5L130 319L97.5 150.5L133.5 187L168.5 229.5L198 274.5L223 319L320.5 572Z",
      fill: "#A8DB53",
      delay: 1.0, // Faster
      duration: 0.55, // Faster
    },
    // Center light leaf
    {
      d: "M364.5 572H181.5L239 300L246 277.5L254.5 255L264 233L274.5 212L285.5 233L295 255L303.5 277.5L310.5 300L364.5 572Z",
      fill: "#EDF8C7",
      delay: 1.2, // Faster
      duration: 0.55, // Faster
    },
    // Far right dark green leaf
    {
      d: "M295 572H389L492.5 367.5L554.5 187L512.5 220.5L468.5 263L404.5 344L295 572Z",
      fill: "#1DA35D",
      delay: 1.4, // Faster
      duration: 0.55, // Faster
    },
    // Far left green leaf
    {
      d: "M259 572H171L72 381.5L0.5 189L81 259L150.5 346L259 572Z",
      fill: "#4CB14B",
      delay: 1.6, // Faster
      duration: 0.55, // Faster
    },
    // Bottom left green leaf
    {
      d: "M282 572H171L121 482L19.5 342L60.5 354.5L99.5 373.5L177 427.5L217.5 475L282 572Z",
      fill: "#54B44C",
      delay: 1.8, // Faster
      duration: 0.45, // Faster
    },
    // Bottom right green leaf
    {
      d: "M265 572L324 478.5L361 441.5L403.5 408L464 378L495.5 367L528 358L488.5 409L452.5 462L389 572H265Z",
      fill: "#7FC64A",
      delay: 2.0, // Faster
      duration: 0.45, // Faster
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center min-h-[500px] bg-[#e6f0e0] rounded-lg">
      <div className="relative w-[300px] h-[300px] sm:w-[400px] sm:h-[400px]" onClick={resetAnimation}>
        <svg viewBox="0 0 555 573" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
          <defs>
            {leaves.map((leaf, index) => (
              <clipPath id={`leaf-clip-${index}`} key={`clip-${index}`}>
                <motion.rect
                  x="0"
                  y="0"
                  width="555"
                  height="573"
                  initial={{ y: 573 }}
                  animate={
                    isComplete
                      ? {
                          y: 0,
                          transition: {
                            delay: leaf.delay,
                            duration: leaf.duration,
                            ease: "easeOut",
                          },
                        }
                      : { y: 573 }
                  }
                />
              </clipPath>
            ))}
          </defs>

          {leaves.map((leaf, index) => (
            <path key={index} d={leaf.d} fill={leaf.fill} style={{ clipPath: `url(#leaf-clip-${index})` }} />
          ))}
        </svg>
      </div>
    </div>
  );
}
